import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/components/StatusBadge";
import {
  Building2, Briefcase, DollarSign, Receipt, Wallet, AlertTriangle,
  FileClock, TimerReset, CheckSquare, Clock10, Users, MapPin, User, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { formatInrIndian as _formatInrIndian } from "@/components/MonthlyInrCard";

// "3 days ago" style relative timestamp — kept small since we only need it for
// the Recent Clients panel and want no runtime dep on date-fns.
function _relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

const SummaryCard = ({ label, value, icon: Icon, onClick, tone = "" }) => (
  <button
    onClick={onClick}
    data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className="text-left bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition-fast"
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`font-heading text-2xl font-semibold mt-1.5 ${tone}`}>{value}</div>
      </div>
      <div className="w-9 h-9 rounded-md bg-slate-50 border border-slate-200 grid place-items-center text-slate-600">
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </button>
);

/**
 * Multi-currency summary card. Shows one line per currency the user actually
 * has activity in. Falls back to a single "USD 0" row if the map is empty so
 * a fresh install still looks intentional. Currencies are sorted with the
 * largest amount first — INR usually leads for Indian entity, USD for LLC.
 */
// Format a large INR number in the Indian short form (crore/lakh). Shared with
// the Revenue page — see /components/MonthlyInrCard for the source.

const MultiCurrencyCard = ({ label, amounts, fallback = 0, icon: Icon, onClick, tone = "", fxRates = {} }) => {
  const entries = amounts && Object.keys(amounts).length
    ? Object.entries(amounts).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    : [["USD", fallback || 0]];
  // Only show an INR-equivalent line when at least one entry is non-INR AND we
  // have a rate for it — otherwise the number would be misleading.
  const inrTotal = entries.reduce((sum, [cur, amt]) => {
    if (!amt) return sum;
    if (cur === "INR") return sum + Number(amt);
    const rate = Number(fxRates?.[cur] || 0);
    return rate > 0 ? sum + Number(amt) * rate : sum;
  }, 0);
  const hasForeign = entries.some(([cur, amt]) => cur !== "INR" && amt);
  const showInr = hasForeign && inrTotal > 0;
  return (
    <button
      onClick={onClick}
      data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="text-left bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition-fast"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
          <div className={`space-y-0.5 mt-1.5 ${tone}`}>
            {entries.map(([cur, amt], idx) => (
              <div
                key={cur}
                data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}-${cur}`}
                className={`font-heading font-semibold whitespace-nowrap ${idx === 0 ? "text-2xl" : "text-sm text-slate-500"}`}
              >
                {formatCurrency(amt, cur)}
              </div>
            ))}
          </div>
          {showInr && (
            <div
              className="text-[11px] text-slate-500 mt-1"
              data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}-inr-total`}
              title="Approximate INR total using cached FX rates"
            >
              ≈ {_formatInrIndian(inrTotal)} total
            </div>
          )}
        </div>
        <div className="w-9 h-9 rounded-md bg-slate-50 border border-slate-200 grid place-items-center text-slate-600 shrink-0 ml-2">
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [fy, setFy] = useState("");
  const [fys, setFys] = useState({ current: "", available: [] });
  const [recentClients, setRecentClients] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [pendingInrCount, setPendingInrCount] = useState(0);
  const [fxRates, setFxRates] = useState({});             // { USD: 83.5, CAD: 61.2, ... }
  const [fxRateDate, setFxRateDate] = useState("");       // ISO date of freshest rate seen
  const navigate = useNavigate();

  const load = async (year) => {
    try {
      const qs = year ? `?financial_year=${year}` : "";
      const { data } = await api.get(`/api/dashboard/summary${qs}`);
      setS(data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    }
  };

  useEffect(() => {
    api.get("/api/dashboard/financial-years").then(({ data }) => {
      setFys(data);
      setFy(data.current || "");
      load(data.current || "");
    }).catch(() => load(""));
    // Recent Clients — /api/clients is already BD-scoped via row-level security
    // (a BD only sees their own clients), so no extra filter needed here.
    api.get("/api/clients")
      .then(({ data }) => {
        const sorted = [...(data || [])].sort((a, b) =>
          (b.created_at || "").localeCompare(a.created_at || ""));
        setRecentClients(sorted.slice(0, 6));
      })
      .catch(() => setRecentClients([]));
    // Recent Projects "awaiting your action" — exclude closed/completed/cancelled
    // so what remains is genuinely something the user still needs to move.
    api.get("/api/projects")
      .then(({ data }) => {
        const stalledStatuses = new Set(["completed", "cancelled"]);
        const open = (data || []).filter((p) =>
          !p.is_closed && !p.is_deleted && !stalledStatuses.has((p.status || "").toLowerCase()));
        open.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setRecentProjects(open.slice(0, 6));
      })
      .catch(() => setRecentProjects([]));
    // "Awaiting INR" banner count — non-INR payments where the accountant hasn't
    // yet recorded the actual INR credited to the bank. Only admin/accounts can
    // edit payments (backend PATCH /payments/{id} requires those roles), so the
    // banner is hidden from BDs — showing it would be un-actionable for them.
    const canBackfillInr = user?.role === "admin" || user?.role === "accounts";
    if (canBackfillInr) {
      api.get("/api/payments")
        .then(({ data }) => {
          const count = (data || []).filter((t) => {
            const cur = (t.currency || "").toUpperCase();
            const inr = Number(t.amount_received_inr || 0);
            return cur && cur !== "INR" && inr <= 0 && !t.is_deleted;
          }).length;
          setPendingInrCount(count);
        })
        .catch(() => setPendingInrCount(0));
    }
    // FX rates for the multi-currency INR strip. Cached server-side per pair
    // for 4h, so hitting all three here is cheap after the first day.
    Promise.all(["USD", "CAD", "AUD"].map((cur) =>
      api.get(`/api/fx/rate?from_ccy=${cur}&to_ccy=INR`).then((r) => [cur, r.data]).catch(() => [cur, null])
    )).then((results) => {
      const rates = {};
      let latestMs = 0;
      let latestIso = "";
      for (const [cur, data] of results) {
        if (data?.rate) {
          rates[cur] = data.rate;
          // rate_date can come as ISO ('2026-08-22T…') or RFC-1123 ('Sat, 22 Aug 2026…')
          // depending on which upstream source served it. Normalise via Date().
          const ts = data.rate_date ? Date.parse(data.rate_date) : NaN;
          if (Number.isFinite(ts) && ts > latestMs) {
            latestMs = ts;
            latestIso = new Date(ts).toISOString();
          }
        }
      }
      setFxRates(rates);
      setFxRateDate(latestIso);
    });
    // Monthly INR moved to the Revenue page — no longer fetched here.
  }, [user?.role]);

  useEffect(() => { if (fy) load(fy); /* eslint-disable-next-line */ }, [fy]);

  const seed = async () => {
    try {
      await api.post("/api/seed-sample-data");
      toast.success("Sample data ready");
      load(fy);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to seed");
    }
  };

  const isBd = user?.role === "business_development";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight" data-testid="dashboard-heading">
            {isBd ? `My Dashboard — ${user?.name?.split(" ")[0] || ""}` : `Welcome, ${user?.name?.split(" ")[0] || ""}`}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isBd
              ? "Personal view — only records you own, filtered by the selected financial year."
              : "Company-wide overview, filtered by the selected financial year (Apr–Mar)."}
          </p>
          {isBd && s?.is_bd_scoped && (
            <div
              className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] px-2 py-0.5"
              data-testid="bd-scope-badge"
            >
              <Users className="w-3 h-3" /> Showing your assigned data only
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2" data-testid="fy-selector">
            <span className="text-xs text-slate-500 uppercase">Financial Year</span>
            <Select value={fy} onValueChange={setFy}>
              <SelectTrigger className="w-32" data-testid="fy-select-trigger">
                <SelectValue placeholder="Select FY" />
              </SelectTrigger>
              <SelectContent>
                {(fys.available || []).map((y) => (
                  <SelectItem key={y} value={y}>{y}{y === fys.current ? " (current)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {user?.role === "admin" && (
            <Button variant="outline" onClick={seed} data-testid="seed-btn">Seed sample data</Button>
          )}
        </div>
      </div>

      {!s ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <>
          {pendingInrCount > 0 && (
            <button
              type="button"
              onClick={() => navigate("/payments?filter=pending-inr")}
              className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 transition text-left"
              data-testid="pending-inr-banner"
              title="Non-INR payments where the actual INR credited to the bank has not been recorded yet"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-sm font-medium text-amber-900">
                    <span data-testid="pending-inr-count">{pendingInrCount}</span> payment{pendingInrCount === 1 ? "" : "s"} awaiting INR
                  </div>
                  <div className="text-[11px] text-amber-800/80">
                    Back-fill the actual INR amount so BD incentives compute on real numbers.
                  </div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-900">
                Review <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard label={isBd ? "My Clients" : "Active Clients"} value={s.active_clients} icon={Building2} onClick={() => navigate("/clients")} />
            <SummaryCard label={isBd ? "My Active Projects" : "Active Projects"} value={s.active_projects} icon={Briefcase} onClick={() => navigate("/projects")} />
            <MultiCurrencyCard label={isBd ? "My Invoiced" : "Total Invoiced"} amounts={s.total_invoiced_by_currency} fallback={s.total_invoiced} icon={Receipt} onClick={() => navigate("/invoices")} fxRates={fxRates} />
            <MultiCurrencyCard label={isBd ? "My Received" : "Amount Received"} amounts={s.total_received_by_currency} fallback={s.total_received} icon={Wallet} tone="text-emerald-700" onClick={() => navigate("/payments")} fxRates={fxRates} />
            <MultiCurrencyCard label={isBd ? "My Outstanding" : "Outstanding"} amounts={s.total_outstanding_by_currency} fallback={s.total_outstanding} icon={FileClock} tone="text-amber-700" onClick={() => navigate("/bd-outstanding")} fxRates={fxRates} />
            <SummaryCard label="Pending Invoices" value={s.pending_invoices_count} icon={TimerReset} onClick={() => navigate("/pending-invoices")} />
            <SummaryCard label="Pending Payments" value={s.pending_payments_count} icon={Clock10} onClick={() => navigate("/pending-payments")} />
            {!isBd && (
              <>
                <SummaryCard label="Await Submission" value={s.projects_awaiting_submission} icon={CheckSquare} />
                <SummaryCard label="Await Approval" value={s.projects_awaiting_approval} icon={CheckSquare} />
              </>
            )}
          </div>
          <Card className="border-slate-200 shadow-none p-6">
            <div className="text-xs text-slate-500">Amounts are grouped by their transaction currency. Toggle FY above to filter by financial year.</div>
            {fxRateDate && !isNaN(new Date(fxRateDate).getTime()) && (
              <div className="text-[11px] text-slate-400 mt-1" data-testid="fx-freshness">
                INR conversions on the cards above use rates as of {new Date(fxRateDate).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}.
              </div>
            )}
          </Card>

          {/* Recent Clients — visible for anyone who has assigned clients.
              For BDs it uses the "Assigned to you" pill so they instantly spot
              their newest accounts; for admins it's a company-wide recent list. */}
          {recentClients.length > 0 && (
            <Card className="border-slate-200 shadow-none p-6" data-testid="recent-clients-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-heading font-semibold text-slate-900">Recent Clients</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {isBd ? "Newest accounts routed to you — jump straight in." : "Latest additions across every BD."}
                  </p>
                </div>
                <button
                  onClick={() => navigate("/clients")}
                  className="text-xs font-medium text-brand hover:text-brand-hover inline-flex items-center gap-1"
                  data-testid="recent-clients-view-all"
                >
                  View all <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {recentClients.map((c) => {
                  const ownedByMe = isBd && c.business_development_owner_id === user?.id;
                  const location = [c.city, c.country].filter(Boolean).join(", ");
                  return (
                    <button
                      key={c.id}
                      onClick={() => navigate("/clients")}
                      className="group text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-brand/50 hover:shadow-sm transition-fast"
                      data-testid={`recent-client-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-heading font-semibold text-slate-900 truncate group-hover:text-brand-dark">
                            {c.firm_name || "Unnamed client"}
                          </div>
                          {c.contact_person_name && (
                            <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1">
                              <User className="w-3 h-3 shrink-0" />
                              {c.contact_person_name}
                            </div>
                          )}
                          {location && (
                            <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1">
                              <MapPin className="w-3 h-3 shrink-0" />
                              {location}
                            </div>
                          )}
                        </div>
                        <Building2 className="w-4 h-4 text-slate-400 group-hover:text-brand shrink-0" />
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        {ownedByMe ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-tint text-brand-dark border border-brand/40 text-[10px] font-medium"
                            data-testid={`recent-client-mine-${c.id}`}
                          >
                            Assigned to you
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">
                            {c.assigned_to_ts ? "Assigned to TS" : (c.business_development_owner_name_snapshot || "Unassigned")}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400 shrink-0" data-testid={`recent-client-age-${c.id}`}>
                          {_relativeTime(c.created_at)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Recent Projects — the 6 most recent OPEN projects (not completed/
              cancelled/closed). For BD users this is naturally scoped to their
              projects via RLS; admins see the company-wide latest. */}
          {recentProjects.length > 0 && (
            <Card className="border-slate-200 shadow-none p-6" data-testid="recent-projects-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-heading font-semibold text-slate-900">Recent Projects</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {isBd ? "Your latest projects still awaiting action." : "Latest projects still awaiting action."}
                  </p>
                </div>
                <button
                  onClick={() => navigate("/projects")}
                  className="text-xs font-medium text-brand hover:text-brand-hover inline-flex items-center gap-1"
                  data-testid="recent-projects-view-all"
                >
                  View all <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {recentProjects.map((p) => {
                  const ownedByMe = isBd && p.business_development_owner_id === user?.id;
                  const statusLabel = (p.status || "in-progress").replace(/_/g, " ");
                  const outstanding = Number(p.total_outstanding || 0);
                  return (
                    <button
                      key={p.id}
                      onClick={() => navigate("/projects")}
                      className="group text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-brand/50 hover:shadow-sm transition-fast"
                      data-testid={`recent-project-${p.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">
                            {p.project_code || "—"}
                          </div>
                          <div className="font-heading font-semibold text-slate-900 truncate group-hover:text-brand-dark mt-0.5">
                            {p.project_name || "Untitled project"}
                          </div>
                          {p.firm_name_snapshot && (
                            <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1">
                              <Building2 className="w-3 h-3 shrink-0" />
                              {p.firm_name_snapshot}
                            </div>
                          )}
                        </div>
                        <Briefcase className="w-4 h-4 text-slate-400 group-hover:text-brand shrink-0" />
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-medium capitalize">
                            {statusLabel}
                          </span>
                          {ownedByMe && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-tint text-brand-dark border border-brand/40 text-[10px] font-medium"
                              data-testid={`recent-project-mine-${p.id}`}
                            >
                              Assigned to you
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 shrink-0 flex items-center gap-2">
                          {outstanding > 0 && (
                            <span className="text-amber-700 font-medium" title="Amount outstanding">
                              {formatCurrency(outstanding, p.currency || "USD")} due
                            </span>
                          )}
                          <span className="text-slate-400" data-testid={`recent-project-age-${p.id}`}>
                            {_relativeTime(p.created_at)}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
